'use client';
import { CSSProperties, MouseEvent, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { IWithClassname } from '@/models/common';
import s from './Tabs.module.scss';
import { measureScrollbar } from '@/utils/measureScrollbar';

export interface Tab<Key> {
  name: string;
  key: Key;
}

interface Props<Key, T> extends IWithClassname {
  tabs: (T & Tab<Key>)[];
  activeTabKey: Key;

  setActiveTab(activeTab: T & Tab<Key>): void;
}

export const Tabs = <Key, T>({ tabs, activeTabKey, setActiveTab, className }: Props<Key, T>) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [customStyles, setCustomStyles] = useState<CSSProperties>({});

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      if (!listRef.current || !wrapperRef.current) return;

      const parentWidth = wrapperRef.current.getBoundingClientRect().width;
      const listWidth = listRef.current.getBoundingClientRect().width;

      if (parentWidth < listWidth) {
        const clientWidth = entry.contentRect.width;
        const scrollbarWidth = measureScrollbar();

        const sideGap = (clientWidth - parentWidth) / 2;

        setCustomStyles({
          minWidth: `calc(100vw - ${scrollbarWidth}px)`,
          maxWidth: '100vw',
          marginLeft: `-${sideGap}px`,
          paddingLeft: `${sideGap}px`,
          paddingRight: `${sideGap}px`,
        });
      } else {
        setCustomStyles({});
      }
    });

    observer.observe(document.body);

    return () => observer.disconnect();
  }, [listRef.current, wrapperRef.current]);

  const onTabClick = (e: MouseEvent<HTMLButtonElement>, tab: T & Tab<Key>) => {
    const element = e.target as HTMLButtonElement;

    element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    setActiveTab(tab);
  };

  return (
    <div ref={wrapperRef} className={clsx(s.wrapper, className)}>
      <div className={clsx(s.tabs, className)} style={customStyles}>
        <ul ref={listRef} className={s.tabs__list}>
          {tabs.map((tab, i) => {
            return (
              <li key={i}>
                <button
                  className={clsx(s.tabs__item, { [s.tabs__item_active]: tab.key === activeTabKey })}
                  onClick={(e) => onTabClick(e, tab)}
                >
                  {tab.name}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};